import React, { Fragment, useEffect, useRef, useState } from "react";
import { useEffectOnce } from "../hooks/useeffectonce";
import { useSelector, useDispatch } from "react-redux";
import { setCounter } from "../redux/slices/notificationscounterslice";
import { status } from "../redux/slices/dictaphoneslice";
import { api } from "../api/httpclient";
import {
  NavLink,
  matchRoutes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  APP_HEADER_HEIGHT,
  BILLING_PAYMENT_MESSAGE,
  BLOCK_DIRTY_MESSAGE_NAVIGATE,
} from "../settings";
import { AppBar, AppBarSection } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import { Badge } from "@progress/kendo-react-indicators";
import { CurrentUser } from "./currentuser";

//import the logos
import logo_bi_white from "../logos/logo_bi_img_white.png"; //"../logos/logo_bi_white.png"

//import icons
import * as KendoIcons from "@progress/kendo-svg-icons";

import { SvgIcon } from "@progress/kendo-react-common";

import "../styles/appheader.css";
import {
  setSideBarVisible,
  setSideBarDetailVisible,
  setLanguage,
} from "../redux/slices/appslice";
import { setActiveSubMenu } from "../redux/slices/menuslice";
import { setClosePaymentNotification } from "../redux/slices/currentuserslice";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import {
  canAccessAlerts,
  canAccessDataCorrection,
  canAccessDataSet,
  canAccessDataSources,
  canAccessExpressions,
  canAccessReports,
} from "../routing/authorizationhandlers";
import { setAppearance } from "../redux/slices/appearanceslice";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import LanguageSelect from "./LanguageSelect";

//not implemented here
// export const NoConnection = props => {
//   const internetStatus = useSelector(state => state.internetStatus);
//   const currentTheme = useSelector(state => state.currentTheme);

//   if (internetStatus == true)
//       return React.Fragment;

//   return (
//     <React.Fragment>
//       <AppBarSection>
//         <span className="k-icon k-i-unlink-horizontal" style={{color: currentTheme.error, fontSize: 20, margin: 8}} />
//       </AppBarSection>

//       <AppBarSection>
//         <span className="k-appbar-separator" />
//       </AppBarSection>
//     </React.Fragment>
//   );
// };

//const logos = {1: logo_1, 2: logo_2, 3: logo_3, 21: logo_21, 22: logo, };

const getMenuItems = (currentUser, currentPath, t) => {
  const dataIcon = KendoIcons.dataIcon;
  const folderIcon = KendoIcons.folderIcon;
  const exclamationCircleIcon = KendoIcons.exclamationCircleIcon;
  const dataSqlIcon = KendoIcons.dataSqlIcon;
  const chartLineMarkersIcon = KendoIcons.chartLineMarkersIcon;
  const wrenchIcon = KendoIcons.wrenchIcon;
  const editToolsIcon = KendoIcons.editToolsIcon;

  if (
    !currentUser?.IsAuthenticated &&
    !currentPath.startsWith("/subscription")
  ) {
    return [
      { id: 1, title: t("header.home"), path: "/#home", icon: null },
      { id: 2, title: t("header.services"), path: "/#services", icon: null },
      { id: 3, title: t("header.features"), path: "/features", icon: null },
      { id: 4, title: t("header.pricing"), path: "/#pricing", icon: null },
      { id: 5, title: t("header.contact"), path: "/#contact", icon: null },
    ];
  }

  // Subscription-specific menu (wildcard match)
  if (
    !currentUser?.IsAuthenticated &&
    currentPath.startsWith("/subscription")
  ) {
    return [
      { id: 1, title: t("header.data"), path: "", icon: dataIcon },
      {
        id: 2,
        title: t("header.alerts"),
        path: "",
        icon: exclamationCircleIcon,
      },
      {
        id: 3,
        title: t("header.reports"),
        path: "",
        icon: chartLineMarkersIcon,
      },
    ];
  }

  let items = [];

  items.push({
    id: 27,
    title: t("header.data"),
    icon: dataIcon,
    submenu: [
      {
        id: 271,
        title: t("header.datasets"),
        path: "/datasets",
        icon: folderIcon,
        canAccess: canAccessDataSet(currentUser),
      },
      {
        id: 12,
        title: t("header.datasources"),
        path: "/datasources",
        icon: dataIcon,
        canAccess: canAccessDataSources(currentUser),
      },
      {
        id: 10,
        title: t("header.expressions"),
        path: "/expressions",
        icon: dataSqlIcon,
        canAccess: canAccessExpressions(currentUser),
      },
      {
        id: 272,
        title: t("header.datacorrection"),
        path: "/datacorrection",
        icon: editToolsIcon,
        canAccess: canAccessDataCorrection(currentUser),
      },
    ].filter((item) => item.canAccess),
  });

  if (canAccessAlerts(currentUser)) {
    items.push({ id: 28, separator: true });
    items.push({
      id: 14,
      title: t("header.alerts"),
      path: "/alerts",
      icon: exclamationCircleIcon,
    });
  }

  if (canAccessReports(currentUser)) {
    items.push({ id: 16, separator: true });
    items.push({
      id: 17,
      title: t("header.reports"),
      path: "/reports",
      icon: chartLineMarkersIcon,
    });
  }

  if (
    currentUser?.Subscription?.Name?.toLowerCase() === "model" &&
    currentUser?.Extras?.Migrations?.CanAccess
  ) {
    items.push({ id: 20, separator: true });
    items.push({
      id: 25,
      title: t("header.migrations"),
      path: "/migrations",
      icon: wrenchIcon,
    });
  }

  return items;
};

export const AppHeader = (props) => {
  const dispatch = useDispatch();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dictaphone = useSelector((state) => state.dictaphone);
  const currentTheme = useSelector((state) => state.currentTheme);
  const currentUser = useSelector((state) => state.currentUser);
  const appearance = useSelector((state) => state.appearance);
  const isLoggedOut = !currentUser?.IsAuthenticated;
  const _currentUser = useRef(null);
  _currentUser.current = currentUser;
  const currentPageState = useSelector((state) => state.currentPage);
  const accManagementPageState = useSelector(
    (state) => state.accountManagementPage,
  );
  const currentPath = useLocation().pathname;
  const dictaphoneOn = dictaphone.status == 1;

  const internetStatus = useSelector((state) => state.internetStatus);
  const appBarBorderBottom = currentTheme.appBarBorderBottom;

  const resolutionHeight = window.screen.height;
  const appBarHeight = APP_HEADER_HEIGHT; // to have a flexible height, can use this -> resolutionHeight * 0.05;
  //let logo = dictaphoneOn ? (currentTheme.darkTheme ? logo_ai_red : logo_ai_white) : logo_white;//logos[currentTheme.id.toString()];
  let logo = currentTheme.darkTheme ? logo_bi_white : logo_bi_white;

  const bellIcon = KendoIcons.bellIcon;

  //get current path
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isHomePage = location.pathname == "/";
  const isLoginPage = location.pathname == "/login";
  const isAccountManagement = location.pathname == "/accountmanagement";
  const isReport =
    location.pathname.toLowerCase().indexOf("/reportingdesign") >= 0 ||
    location.pathname.toLowerCase().indexOf("/reportingview") >= 0;

  /*menu*/
  const menu = useSelector((state) => state.menu);
  const menuRef = useRef(null);

  const handleClickOutside = (event) => {
    if (
      menuRef.current &&
      menuRef.current.contains &&
      !menuRef.current.contains(event.target)
    ) {
      if (menu.activeSubMenu !== null) {
        dispatch(setActiveSubMenu(null));
      }
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [menu.activeSubMenu]);

  const handleToggleSubMenu = (item) => {
    dispatch(setActiveSubMenu(menu.activeSubMenu === item.id ? null : item.id));
  };

  /*side bar*/
  const sideBarVisible = useSelector((state) => state.app.sideBarVisible);
  const sideBarDetailVisible = useSelector(
    (state) => state.app.sideBarDetailVisible,
  );

  const handleOnBurgerClick = (e) => {
    if (
      !_currentUser.current ||
      !_currentUser.current.IsAuthenticated ||
      !_currentUser.current.ReportsMenu ||
      _currentUser.current.ReportsMenu.length < 1
    )
      return;

    if (sideBarDetailVisible) dispatch(setSideBarDetailVisible(false));

    dispatch(setSideBarVisible(!sideBarVisible));
  };
  /*side bar*/

  const handleCloseNotification = () => {
    dispatch(setClosePaymentNotification());
  };

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, location.hash]);

  return (
    <>
      <AppBar
        themeColor="inherit"
        className={`app-header ${
          isLoggedOut ? "public-mobile-menu" : ""
        } ${mobileMenuOpen ? "mobile-menu-open" : ""}`}
      >
        <AppBarSection className="company">
          {_currentUser.current && _currentUser.current.IsAuthenticated && (
            <Button
              className="site-burger"
              style={{ marginTop: -5, color: currentTheme.burgerColor }}
              icon="menu"
              fillMode="flat"
              onClick={handleOnBurgerClick}
            />
          )}
          {isLoggedOut && (
            <Button
              className="mobile-menu-toggle"
              aria-label={t("header.menu")}
              aria-expanded={mobileMenuOpen}
              icon="menu"
              fillMode="flat"
              onClick={() => setMobileMenuOpen((isOpen) => !isOpen)}
            />
          )}
          <NavLink
            to="/"
            reloadDocument={false}
            className="intelligence"
            onClick={(e) => {
              if (currentPath.startsWith("/subscription")) {
                e.preventDefault(); // block navigation
                e.stopPropagation(); // block sub-menu logic
              }
            }}
            style={{
              pointerEvents: currentPath.startsWith("/subscription")
                ? "none"
                : "auto",
              cursor: currentPath.startsWith("/subscription")
                ? "default"
                : "pointer",
            }}
          >
            Intelligence
          </NavLink>
          <Fragment>
            <LanguageSelect
              currentTheme={currentTheme}
              appearance={appearance}
              currentUser={_currentUser}
            />
            {!isLoggedOut && (
              <NavLink
                to="/notifications"
                className="mobile-notifications-link"
                aria-label={t("homepageuser.notifications")}
              >
                <SvgIcon icon={bellIcon} />
              </NavLink>
            )}
          </Fragment>
        </AppBarSection>
        <div ref={menuRef}>
          <AppBarSection className="menu">
            <ul className="main">
              {getMenuItems(currentUser, currentPath, t).map((item, index) => {
                if (item.separator)
                  return (
                    <li
                      key={item.id}
                      style={{ display: "flex", margin: " 0 4px" }}
                    >
                      {<div className="separator">&nbsp;</div>}
                    </li>
                  );
                else if (item.submenu)
                  return (
                    <li
                      key={item.id}
                      style={{
                        margin: "0 2px",
                      }}
                    >
                      <div
                        className="link"
                        onClick={(e) => {
                          handleToggleSubMenu(item);
                        }}
                      >
                        <div
                          className="item"
                          id={`${
                            isReport && item.id == 17
                              ? "isReport"
                              : "isNotReport"
                          }`}
                          style={{
                            position: "static",
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: "uppercase",
                          }}
                        >
                          {item.icon && <SvgIcon icon={item.icon} />}
                          <span className="item-link">{item.title}</span>
                        </div>
                        <ul
                          className={`sub-menu ${
                            menu.activeSubMenu === item.id ? "show" : "hide"
                          }`}
                          id={`submenu-${item.id}`}
                        >
                          {item.submenu.map((subitem) =>
                            subitem.canAccess ? (
                              <li key={subitem.id}>
                                <NavLink
                                  to={subitem.path}
                                  className="link"
                                  onClick={(e) => {
                                    if (
                                      currentPath.startsWith("/subscription")
                                    ) {
                                      e.preventDefault(); // block navigation
                                      e.stopPropagation(); // block sub-menu logic
                                    }
                                  }}
                                  style={{
                                    pointerEvents: currentPath.startsWith(
                                      "/subscription",
                                    )
                                      ? "none"
                                      : "auto",
                                    cursor: currentPath.startsWith(
                                      "/subscription",
                                    )
                                      ? "default"
                                      : "pointer",
                                  }}
                                >
                                  {subitem.icon && (
                                    <SvgIcon
                                      icon={subitem.icon}
                                      className="icon"
                                    />
                                  )}
                                  <span>{subitem.title}</span>
                                </NavLink>
                              </li>
                            ) : null,
                          )}
                        </ul>
                      </div>
                    </li>
                  );
                else
                  return (
                    <li key={item.id} style={{ margin: "0 2px" }}>
                      <NavLink
                        reloadDocument={item.id == 9999}
                        to={item.path}
                        className={({ isActive }) => {
                          const isExactMatch =
                            location.pathname + location.hash === item.path;
                          return `link ${isExactMatch ? "active" : ""}`;
                        }}
                        onClick={(e) => {
                          //console.log("onclick", item);
                          if (currentPath.startsWith("/subscription")) {
                            e.preventDefault(); // block navigation
                            e.stopPropagation(); // block sub-menu logic
                          }
                          handleToggleSubMenu(item);

                          const [basePath, sectionId] = item.path.split("#");

                          if (basePath !== location.pathname) {
                            navigate(item.path);
                          } else if (sectionId) {
                            window.location.hash = sectionId;
                            const section = document.getElementById(sectionId);
                            if (section) {
                              section.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                              });
                            }
                          }

                          if (currentPageState.id && currentPageState.isDirty) {
                            if (
                              window.confirm(BLOCK_DIRTY_MESSAGE_NAVIGATE) ==
                              false
                            ) {
                              e.stopPropagation();
                              e.preventDefault();
                              return false;
                            }
                          }
                        }}
                        style={{
                          pointerEvents: currentPath.startsWith("/subscription")
                            ? "none"
                            : "auto",
                          cursor: currentPath.startsWith("/subscription")
                            ? "default"
                            : "pointer",
                        }}
                      >
                        <div
                          className="item"
                          id={`${
                            isReport && item.id == 17
                              ? "isReport"
                              : "isNotReport"
                          }`}
                          style={{
                            position: item.id == 19 ? "relative" : "static",
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: "uppercase",
                          }}
                        >
                          {item.icon && <SvgIcon icon={item.icon} />}
                          <span className="item-link">{item.title}</span>
                        </div>
                      </NavLink>
                    </li>
                  );
              })}
            </ul>
          </AppBarSection>
        </div>
        <div className="right">
          <AppBarSection className="right">
            <CurrentUser />
          </AppBarSection>
        </div>
        <style>
          {` 
          .app-header.k-appbar{       
            height: ${appBarHeight}px;
            border-bottom: ${currentTheme.appBarBorderBottom};
            background-color: ${
              isHomePage || isLoginPage
                ? currentTheme.appBarHomeBg
                : currentTheme.appBarBg
            };
            background-image: ${
              isHomePage || isLoginPage
                ? currentTheme.appBarHomeBgImage
                : currentTheme.appBarBgImage
            };
             flex-direction:${appearance.isRightToLeft ? "row-reverse" : "row"};
          }

          .app-header .k-appbar-section.menu
          {
            margin-top: 0px;/*was -6px*/
            flex-direction:${appearance.isRightToLeft ? "row-reverse" : "row"};
          }
  
          .app-header .company
          {
            /*the div containing the logo*/
            margin-left: 8px;
            margin-top: 6px;flex-direction:${
              appearance.isRightToLeft ? "row-reverse" : "row"
            };
          }

          .app-header .company img
          {
            /*the logo*/
            margin-top: 0px;
            height: 18px;/*${appBarHeight}px;*/
          }

          .app-header .company .intelligence
          {
            /*the title*/
            color: ${currentTheme.appBarLinkColor};
            font-weight: 900;
            font-size: 14px;
            display: block;
            margin-left: 10px;
            margin-right: 10px;
            margin-top: -7px;
            cursor:default;
          }

          .app-header .link
          {
            /*the <a> of every menu item*/
            font-family: helvetica;
            letter-spacing: 1px;
            font-weight: 600;
            font-size: 12px;
            margin-left: 6px;
            margin-right: 6px;
          }

          .app-header .link .item
          {
            /*the <div> inside the <a>*/     
            flex-direction:${
              appearance.isRightToLeft ? "row-reverse" : "row"
            };       
          }
          
          .app-header .link .item, .app-header .link .item span
          {
            /*the div containing the icon and the span containing the menu title*/
            color: ${currentTheme.appBarLinkColor}
          }

          .app-header .link .item .item-link{
          
             ${
               appearance.isRightToLeft
                 ? "padding-right: 6px;"
                 : "padding-left: 6px;"
             }
          }

          .app-header .item#isReport span
          {
            /*the menu title*/
            /*font-weight: 500;*/
            color:${currentTheme.appBarLinkActiveColor};            
          }

          .app-header .link .item:hover span
          {
            /*when item is hovered, refers to both icon and text as each is a span*/
            cursor: pointer;
            color:${currentTheme.appBarLinkActiveColor};            
          }

          .app-header .link.active .item, .app-header .link.active .item span
          {
              color: ${currentTheme.appBarLinkActiveColor};
              /*font-weight: 700!important;*/
          }

          .app-header .link .k-svg-icon
          {
            /*the menu item icon*/
            font-size: 18px;
          }

          .app-header .separator
          {
            width: 1px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            border-left: 1px solid ${currentTheme.appBarLinkColor};
            color: ${currentTheme.appBarLinkColor};
          }

          .app-header .k-appbar-section.right
          {
            margin-top: 0px;/*was -8px*/
            margin-right: 18px;
           
          }

          .app-header .current-user-email
          {            
            color: ${currentTheme.appBarLinkColor};
            font-size: 13px;
            font-weight: 400;
            width: 100%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          
          .app-header .current-subscription-name
          {
            color: ${currentTheme.appBarLinkColor};
            font-size: 10px;
            font-weight: 600;
            width: 100%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .app-header ul{
          flex-direction:  ${appearance.isRightToLeft ? "row-reverse" : "row"}
          }

          .app-header ul.sub-menu {
              position: absolute;
              color: ${currentTheme.appBarLinkColor};
              z-index: 1000;
              height:fit-content;
              border-radius: 0px;
              padding: 10px;
              font-weight: normal;
              width: max-content;
              min-width:100px;
              top: ${appBarHeight}px;
              border-bottom: ${currentTheme.appBarBorderBottom};
              background-color: ${
                isHomePage || isLoginPage
                  ? currentTheme.appBarHomeBg
                  : currentTheme.appBarBg
              };
              background-image: ${
                isHomePage || isLoginPage
                  ? currentTheme.appBarHomeBgImage
                  : currentTheme.appBarBgImage
              };
              margin-left:-12px;
          }

           .app-header ul.sub-menu.show {
              display: block !important;
          }

           .app-header ul.sub-menu.hide {
              display: none !important;
          }

           .app-header ul.sub-menu {
              padding: 0;
              padding-inline-start: 0;
              margin-block-start: 0;
              margin-block-end: 0;
          }

           .app-header ul.sub-menu li {
              list-style: none;
              margin: 10px;
              display: flex;
            flex-direction:${appearance.isRightToLeft ? "row-reverse" : "row"};
          }
          .app-header ul.sub-menu li a {
              color: ${currentTheme.appBarLinkColor};
              text-decoration:none;       display: flex;
            flex-direction:${appearance.isRightToLeft ? "row-reverse" : "row"};
          }
          .app-header ul.sub-menu .link .icon{
          ${
            appearance.isRightToLeft
              ? " margin-left:10px;"
              : " margin-right:10px;"
          };
             
              margin-top:-3px
          }

          .app-header ul.sub-menu li a:hover span
          {
            /*when item is hovered, refers to both icon and text as each is a span*/
            cursor: pointer;
            color:${currentTheme.appBarLinkActiveColor};            
          }

          .app-header .buttons-container
          {
                flex-direction:${
                  appearance.isRightToLeft ? "row-reverse" : "row"
                };
          }

          .app-header .mobile-menu-toggle {
            display: none;
            color: ${currentTheme.appBarLinkColor};
          }

          .app-header .mobile-notifications-link {
            display: none;
            align-items: center;
            color: ${currentTheme.appBarLinkColor};
            margin-top: -5px;
            margin-inline-start: 8px;
          }

          @media (max-width: 1024px) {
            .app-header.k-appbar {
              height: ${appBarHeight}px;
              min-height: ${appBarHeight}px;
              flex-wrap: nowrap;
              padding: 0 8px;
              position: relative;
              overflow: visible;
            }

            .app-header .mobile-menu-toggle {
              display: inline-flex;
              margin-top: -5px;
            }

            .app-header .company {
              margin-left: 0;
              min-width: 0;
            }

            .app-header .company .intelligence {
              font-size: 13px;
              margin-left: 6px;
              margin-right: 6px;
            }

            .app-header .mobile-notifications-link {
              display:block;
              position: absolute;
              right:45px;
              margin-top:0;
            }

            .app-header .k-appbar-section.menu {
              display: flex;
              position: absolute;
              z-index: 1000;
              top: ${appBarHeight}px;
              left: -100%;
              width: fit-content;
              max-width: calc(100vw - 24px);
              margin: 0;
              max-height: 0;
              overflow: hidden;
              padding: 0 8px;
              opacity: 0;
              pointer-events: none;
              transition: left 250ms ease, max-height 250ms ease,
                padding 250ms ease, opacity 200ms ease;
              background-color: ${
                isHomePage || isLoginPage
                  ? currentTheme.appBarHomeBg
                  : currentTheme.appBarBg
              };
              border-top: 0;
            }

            .app-header.public-mobile-menu.mobile-menu-open .k-appbar-section.menu {
              left: 0;
              max-height: 600px;
              padding: 8px;
              opacity: 1;
              pointer-events: auto;
              border-top: ${currentTheme.appBarBorderBottom};
            }

            .app-header:not(.public-mobile-menu) .k-appbar-section.menu {
              display: none;
            }

            .app-header .k-appbar-section.menu .main {
              width: fit-content;
              min-width: 180px;
              max-width: calc(100vw - 24px);
              display: flex;
              flex-direction: column;
              align-items: stretch;
              gap: 2px;
            }

            .app-header .k-appbar-section.menu .main > li {
              margin: 0 !important;
              width: 100%;
            }

            .app-header .k-appbar-section.menu .main > li .link {
              display: flex;
              min-height: 40px;
              align-items: center;
              margin: 0;
              padding: 0 10px;
            }

            .app-header .separator {
              display: none;
            }

            .app-header ul.sub-menu {
              position: static;
              width: 100%;
              margin: 0;
              background: transparent;
              border: 0;
              box-shadow: none;
            }

            .app-header ul.sub-menu li {
              margin: 0;
            }

            .app-header ul.sub-menu li a {
              width: 100%;
              min-height: 38px;
              padding: 0 20px;
              align-items: center;
            }

            .app-header .right {
              margin-left: auto;
              min-width: 0;
            }

            .app-header .k-appbar-section.right {
              margin-right: 0;
            }

            .app-header .current-user-email,
            .app-header .current-subscription-name {
              max-width: 120px;
            }
          }

        `}
        </style>
      </AppBar>
      {_currentUser.current.IsAuthenticated &&
        _currentUser.current.Subscription?.IsStopped &&
        accManagementPageState.selectedTab != 3 && (
          <>
            <div className="billingnotification">
              <div className="txt">
                <SvgIcon
                  icon={bellIcon}
                  size="small"
                  style={{ margin: "6px" }}
                />
                {BILLING_PAYMENT_MESSAGE}
              </div>
              <div>
                <NavLink to="/accountmanagement" className="btnPay">
                  Pay Now
                </NavLink>
                <span
                  onClick={handleCloseNotification}
                  className="closePaymentNotification"
                >
                  x
                </span>
              </div>
            </div>

            <style>
              {`
      
            .billingnotification {
              padding: 5px 20px 8px;
              background-color: rgba(255, 0, 0, 0.4);
              color: #fff;
              display: flex;
              align-content: center;
              justify-content: space-between;
              overflow: hidden; /* Ensures smooth scrolling effect */
              white-space: nowrap; /* Prevents text from wrapping */
              display: ${
                _currentUser.current.IsAuthenticated
                  ? _currentUser.current?.Subscription?.IsStopped
                    ? "flex"
                    : "flex"
                  : "none" //todo:change it to none
              } 
            }

            .billingnotification .btnPay{
               color: #fff ; 
               backgroundColor:${currentTheme.touchColor};
               border: 1px solid #fff;
               border-radius:3px;
                padding:2px 10px 3px;
                cursor:pointer;
                margin-right:20px;
            }

            .billingnotification .closePaymentNotification{
                cursor:pointer;
                font-size:20px
            }

            .billingnotification .txt{
                padding-top:3px
            }
    
         `}
            </style>
          </>
        )}
    </>
  );
};
